import React, { createContext, useContext, useEffect, useState } from 'react';
import { apiClient } from '../services/api';

export interface BrandingConfig {
  app_name: string;
  logo_url: string | null;
  primary_color: string | null;
  secondary_color: string | null;
  favicon_url: string | null;
}

const DEFAULT_BRANDING: BrandingConfig = {
  app_name: 'ModalOS',
  logo_url: null,
  primary_color: null,
  secondary_color: null,
  favicon_url: null,
};

const BrandingContext = createContext<BrandingConfig>(DEFAULT_BRANDING);

export function useBranding() {
  return useContext(BrandingContext);
}

/**
 * Attempt to derive light/dark shades from a hex colour.
 * Falls back to the CSS defaults when the colour is not a valid hex string.
 */
function hexToShades(hex: string): {
  light: string;
  mid: string;
  base: string;
  dark: string;
  surfaceTint: string;
  infoContainer: string;
} | null {
  const m = hex.match(/^#?([0-9a-f]{6})$/i);
  if (!m) return null;
  const r = parseInt(m[1].substring(0, 2), 16);
  const g = parseInt(m[1].substring(2, 4), 16);
  const b = parseInt(m[1].substring(4, 6), 16);
  return {
    light: `rgba(${r}, ${g}, ${b}, 0.08)`,
    mid: `rgba(${r}, ${g}, ${b}, 0.75)`,
    base: hex.startsWith('#') ? hex : `#${hex}`,
    dark: `rgb(${Math.round(r * 0.8)}, ${Math.round(g * 0.8)}, ${Math.round(b * 0.8)})`,
    surfaceTint: `rgba(${r}, ${g}, ${b}, 0.07)`,
    infoContainer: `rgba(${r}, ${g}, ${b}, 0.10)`,
  };
}

function applyBrandingToDOM(branding: BrandingConfig) {
  const root = document.documentElement;

  // Apply primary colour CSS variables
  if (branding.primary_color) {
    const shades = hexToShades(branding.primary_color);
    if (shades) {
      root.style.setProperty('--brand-primary', shades.base);
      root.style.setProperty('--brand-primary-light', shades.light);
      root.style.setProperty('--brand-primary-mid', shades.mid);
      root.style.setProperty('--brand-primary-dark', shades.dark);
      // Propagate to design system tokens
      root.style.setProperty('--primary', shades.base);
      root.style.setProperty('--primary-container', shades.mid);
      root.style.setProperty('--primary-hover', shades.dark);
      // Derive tinted surface and info-container from brand primary
      root.style.setProperty('--surface-container-low', shades.surfaceTint);
      root.style.setProperty('--info-container', shades.infoContainer);
    }
  }

  if (branding.secondary_color) {
    root.style.setProperty('--brand-secondary', branding.secondary_color);
  }

  // Apply favicon
  if (branding.favicon_url) {
    let link = document.querySelector("link[rel~='icon']") as HTMLLinkElement | null;
    if (!link) {
      link = document.createElement('link');
      link.rel = 'icon';
      document.head.appendChild(link);
    }
    link.href = branding.favicon_url;
  }

  const path = window.location?.pathname || '';
  const hasRouteSpecificTitle =
    path.startsWith('/pay/request/')
    || path.startsWith('/pay/invoice/');

  // Apply generic page title only when a route-specific public title is not responsible for it.
  if (branding.app_name && !hasRouteSpecificTitle) {
    document.title = branding.app_name;
  }
}

export function BrandingProvider({ children }: { children: React.ReactNode }) {
  const [branding, setBranding] = useState<BrandingConfig>(DEFAULT_BRANDING);

  useEffect(() => {
    // Only fetch branding for company tenants (not globaladmin)
    const isPlatform =
      typeof window !== 'undefined' &&
      window.location?.hostname?.toLowerCase().startsWith('globaladmin.');

    if (isPlatform) return;

    apiClient
      .get('/config/branding')
      .then((res) => {
        const data = res.data as BrandingConfig;
        setBranding(data);
        applyBrandingToDOM(data);
      })
      .catch(() => {
        // Branding fetch failed — use defaults silently
      });
  }, []);

  return (
    <BrandingContext.Provider value={branding}>
      {children}
    </BrandingContext.Provider>
  );
}
